import type { TError, TMentionUser } from '@onesource/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * Query key for the "Posted by" filter candidates (post authors of a space).
 * Keyed by the search query so each term is cached independently.
 */
export const getSpacePostAuthorsQueryKey = (spaceId: string, query: string) => [
  ENTITY,
  'spaces',
  spaceId,
  'post-authors',
  query,
];

interface UseGetSpacePostAuthorsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch members who have authored posts in a space — the candidates for
 * the feed's "Posted by" filter. Sourced from actual post authors (not the
 * activity-gated global mention cache), so every real poster is selectable and
 * no one who never posted appears. Private profiles are excluded server-side.
 *
 * @param spaceId - Space ID (UUID)
 * @param query - Search term (display name or username); pass '' for the first N
 * @param options - Query options (enabled, etc.) — pass `{ enabled: false }` to defer
 */
export const useGetSpacePostAuthors = (spaceId: string, query: string, options?: UseGetSpacePostAuthorsOptions) => {
  const enabled = options?.enabled ?? true;

  return useQuery<TMentionUser[], TError>({
    queryKey: getSpacePostAuthorsQueryKey(spaceId, query),
    queryFn: async () => {
      const res = await axios.get<TMentionUser[]>(`/community-hub/spaces/${spaceId}/posts/authors`, {
        params: { q: query },
      });
      return res.data;
    },
    enabled: !!spaceId && enabled,
    // Keep the previous term's results on screen while the next term loads so the
    // dropdown doesn't flash empty between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // 1 minute
  });
};
